import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, TrendingUp, Calendar, AlertCircle, Link as LinkIcon, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { nanoid } from "nanoid";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface LenderDashboardProps {
  lenderProfileId?: string;
}

interface DashboardStats {
  totalLenders: number;
  totalFundsReceived: number;
  totalFundsPaid: number;
  totalAccruedInterest: number;
  lenderStats: Array<{
    lender_name: string;
    principal: number;
    accrued_interest: number;
    next_payment_date: string;
    payment_schedule: string;
  }>;
}

export const LenderDashboard = ({ lenderProfileId }: LenderDashboardProps = {}) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAccessLinkDialog, setShowAccessLinkDialog] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [expirationDays, setExpirationDays] = useState(90);
  const [generatedLink, setGeneratedLink] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch lenders (filtered if lenderProfileId is provided)
      let lendersQuery = supabase
        .from("lender_profiles")
        .select("*")
        .eq("is_active", true);
      
      if (lenderProfileId) {
        lendersQuery = lendersQuery.eq("id", lenderProfileId);
      }

      const { data: lenders, error: lendersError } = await lendersQuery;

      if (lendersError) throw lendersError;

      // Fetch transactions (filtered if lenderProfileId is provided)
      let transactionsQuery = supabase
        .from("lender_transactions")
        .select("*");
      
      if (lenderProfileId) {
        transactionsQuery = transactionsQuery.eq("lender_profile_id", lenderProfileId);
      }

      const { data: transactions, error: txnError } = await transactionsQuery;

      if (txnError) throw txnError;

      // Calculate stats
      const totalFundsReceived = transactions
        ?.filter((t) => t.transaction_type === "loan_received")
        .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

      const totalFundsPaid = transactions
        ?.filter((t) =>
          ["interest_payment", "principal_repayment"].includes(t.transaction_type)
        )
        .reduce((sum, t) => sum + Number(t.amount), 0) || 0;

      // Calculate per-lender stats
      const lenderStats = lenders?.map((lender) => {
        const lenderTxns = transactions?.filter(
          (t) => t.lender_profile_id === lender.id
        ) || [];

        const principal = lenderTxns
          .filter((t) => t.transaction_type === "loan_received")
          .reduce((sum, t) => sum + Number(t.amount), 0);

        // Find last repayment
        const lastRepayment = lenderTxns
          .filter((t) =>
            ["interest_payment", "principal_repayment"].includes(t.transaction_type)
          )
          .sort((a, b) =>
            new Date(b.transaction_date).getTime() -
            new Date(a.transaction_date).getTime()
          )[0];

        const startDate = lastRepayment
          ? new Date(lastRepayment.transaction_date)
          : new Date(lender.created_at);
        const today = new Date();
        const daysDiff = Math.floor(
          (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        );

        // Use a default rate since the schema doesn't have agreed_interest_rate
        const dailyRate = 0.05 / 365; // 5% default rate
        const accrued_interest = principal * dailyRate * daysDiff;

        // Calculate next payment date using created_at as start
        const next_payment_date = calculateNextPaymentDate(
          lender.created_at,
          'monthly' // default schedule
        );

        return {
          lender_name: lender.lender_name,
          principal,
          accrued_interest,
          next_payment_date,
          payment_schedule: 'monthly',
        };
      }) || [];

      const totalAccruedInterest = lenderStats.reduce(
        (sum, l) => sum + l.accrued_interest,
        0
      );

      setStats({
        totalLenders: lenders?.length || 0,
        totalFundsReceived,
        totalFundsPaid,
        totalAccruedInterest,
        lenderStats,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateNextPaymentDate = (
    startDate: string,
    schedule: string
  ): string => {
    const start = new Date(startDate);
    const today = new Date();

    let monthsToAdd = 0;
    switch (schedule) {
      case "monthly":
        monthsToAdd = 1;
        break;
      case "quarterly":
        monthsToAdd = 3;
        break;
      case "semi-annually":
        monthsToAdd = 6;
        break;
      case "annually":
        monthsToAdd = 12;
        break;
    }

    let nextDate = new Date(start);
    while (nextDate < today) {
      nextDate.setMonth(nextDate.getMonth() + monthsToAdd);
    }

    return nextDate.toISOString().split("T")[0];
  };

  const generateAccessLink = async () => {
    if (!accessEmail) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    try {
      const token = nanoid(32);
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expirationDays);

      const { error } = await supabase
        .from("lender_dashboard_access")
        .insert({
          email: accessEmail,
          lender_profile_id: null, // null = shows all lenders
          access_token: token,
          expires_at: expiresAt.toISOString(),
        });

      if (error) throw error;

      const link = `${window.location.origin}/lender-dashboard?token=${token}`;
      setGeneratedLink(link);
      toast({
        title: "Success",
        description: "Dashboard access link generated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast({
      title: "Success",
      description: "Link copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const resetLinkDialog = () => {
    setShowAccessLinkDialog(false);
    setAccessEmail("");
    setExpirationDays(90);
    setGeneratedLink("");
    setCopied(false);
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  if (!stats) {
    return <div className="text-center py-8">No data available</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {lenderProfileId ? "Your Lender Dashboard" : "Lender Dashboard"}
          </h3>
          {/* {!lenderProfileId && (
            <Button onClick={() => setShowAccessLinkDialog(true)} variant="outline">
              <LinkIcon className="h-4 w-4 mr-2" />
              Generate Dashboard Access Link
            </Button>
          )} */}
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <AlertCircle className="h-4 w-4" />
              <span>Active Lenders</span>
            </div>
            <p className="text-2xl font-bold">{stats.totalLenders}</p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span>Total Funds Received</span>
            </div>
            <p className="text-2xl font-bold">
              ${stats.totalFundsReceived.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span>Total Funds Paid</span>
            </div>
            <p className="text-2xl font-bold">
              ${stats.totalFundsPaid.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>

          <Card className="p-4 bg-primary/5">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span>Total Accrued Interest</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              ${stats.totalAccruedInterest.toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </p>
          </Card>
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-3">Lender Details</h4>
        <div className="space-y-3">
          {stats.lenderStats.map((lender, idx) => (
            <Card key={idx} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-semibold">{lender.lender_name}</h5>
                  <p className="text-sm text-muted-foreground capitalize">
                    {lender.payment_schedule} payments
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground mb-1">Principal</p>
                  <p className="font-semibold">
                    ${lender.principal.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Accrued Interest</p>
                  <p className="font-semibold text-primary">
                    ${lender.accrued_interest.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">Next Payment</p>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <p className="font-semibold">
                      {new Date(lender.next_payment_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={showAccessLinkDialog} onOpenChange={(open) => !open && resetLinkDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Full Dashboard Access Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!generatedLink ? (
              <>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={accessEmail}
                    onChange={(e) => setAccessEmail(e.target.value)}
                    placeholder="recipient@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="expiration">Expiration (days)</Label>
                  <Input
                    id="expiration"
                    type="number"
                    value={expirationDays}
                    onChange={(e) => setExpirationDays(parseInt(e.target.value))}
                    min={1}
                    max={365}
                  />
                </div>
                <Button onClick={generateAccessLink} className="w-full">
                  Generate Link
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Access Link</Label>
                  <div className="flex gap-2">
                    <Input value={generatedLink} readOnly />
                    <Button onClick={copyToClipboard} variant="outline" size="icon">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This link will expire in {expirationDays} days and shows all lenders' financial data
                  </p>
                </div>
                <Button onClick={resetLinkDialog} variant="outline" className="w-full">
                  Close
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
